import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import { selectInput } from 'components/final-form/SelectInput';
import { textAreaInput } from 'components/final-form/TextAreaInput';
import { textInput } from 'components/final-form/TextInput';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import { IndexedZipData } from 'components/patient/upsert/UpsertPatient';
import { sortZipData } from 'components/patient/utils/patientUtils';
import { FormApi } from 'final-form';
import createDecorator from 'final-form-calculate';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header } from 'semantic-ui-react';
import { editTestLaboratory, saveTestLaboratory } from 'services/testLaboratoryServices';
import { searchZipDataForInput } from 'services/zipDataServices';
import { ZipDataOrderBy } from 'ts-types/api.enums';
import { CountryDto, UpsertPatientDto, UpsertTestLaboratoryDto } from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { IndexSignature } from 'utils/praxTypes';
import { required } from 'utils/validatorUtils';

interface Props extends AuthConsumerRenderProps, MessageSourceProps {

  toggleAddEditModal: () => void,
  onSave: () => void,
  onCancel: () => void,
  testLaboratoryId?: number,
  testLaboratory?: UpsertTestLaboratoryDto,
  countries: Array<CountryDto>

}

interface State {

  errorMessage?: string,
  cities: Array<DropdownOption>,
  zip: Array<DropdownOption>,
  countryOptions: Array<DropdownOption>,
  pageDataLoaded: boolean,
  cancelTokenSource: CancelTokenSource

}

class UpsertTestLaboratoryForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {testLaboratory} = this.props;
    const cancelTokenSource = axios.CancelToken.source();

    const initialCities: Array<DropdownOption> = [];
    const initialZip: Array<DropdownOption> = [];

    if (testLaboratory) {

      if (testLaboratory.city) {
        initialCities.push({
          key: testLaboratory.city,
          text: testLaboratory.city,
          value: testLaboratory.city,
          predefinedOption: true
        })
      }

      if (testLaboratory.zip) {
        initialZip.push({
          key: testLaboratory.zip,
          text: testLaboratory.zip,
          value: testLaboratory.zip,
          predefinedOption: true
        })
      }
    }

    this.state = {
      cities: initialCities,
      zip: initialZip,
      countryOptions: mapToDropdownOptionArray(props.countries, "name", "shortName", "shortName"),
      pageDataLoaded: false,
      cancelTokenSource
    }
  };

  componentWillUnmount(): void {

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  initialTestLaboratoryValues = (): Partial<UpsertTestLaboratoryDto> => {

    return {
      active: true,
      name: "",
      street: "",
      zip: "",
      city: "",
      countryIso: "CH",
      email: "",
      tel: "",
      remarks: "",
      basicLaboratory: false
    };
  };

  mapZipData = (field: string, zipData: Array<IndexedZipData>, orderBy: ZipDataOrderBy): Array<DropdownOption> => {

    const sortedZipData = sortZipData(zipData, orderBy);

    return sortedZipData.map(data => ({
      key: data.id,
      text: data[field],
      value: `${data.zip}, ${data.city}, ${data.countryId}`,
      content: `${data.zip}, ${data.city}, ${data.canton}`,
      predefinedOption: true
    }))
  };

  searchZipData = (query: string, countryId: number) => {

    const {cities, zip: zips, cancelTokenSource} = this.state;

    searchZipDataForInput(query, countryId, cancelTokenSource, 75)
        .then(response => {

          const allInitialAndNewCityAdditions = cities.filter(city => city.value == city.key);
          const allInitialAndNewZipAdditions = zips.filter(zip => zip.value == zip.key);

          this.setState({
            cities: [...allInitialAndNewCityAdditions, ...this.mapZipData("city", response, ZipDataOrderBy.CITY)],
            zip: [...allInitialAndNewZipAdditions, ...this.mapZipData("zip", response, ZipDataOrderBy.ZIP)]
          })
        })
        .catch(() => {
          this.setState({
            cities: [],
            zip: []
          })
        })
  };

  onNewAddition = (options: "zip" | "cities") => (newAddition: DropdownOption) => {

    const {cities, zip: zips} = this.state;

    if (options == "cities") {

      const foundCity = cities.find(city => city.key == newAddition.key);

      if (!foundCity) {
        newAddition.predefinedOption = false;
        const newCities = cities.filter(city => city.predefinedOption === true);
        this.setState({
          cities: [newAddition, ...newCities]
        });
      }
    } else if (options == "zip") {

      const foundZip = zips.find(zip => zip.key == newAddition.key);

      if (!foundZip) {
        newAddition.predefinedOption = false;
        const newZips = zips.filter(city => city.predefinedOption === true);
        this.setState({
          zip: [newAddition, ...newZips]
        });
      }
    }
  };

  updateZipDataFieldDecorator = (field: string) =>
      (zipData?: string, allValues?: Partial<UpsertPatientDto & IndexSignature>) => {

        if (zipData) {
          const splitZipData = zipData.split(", ");

          if (splitZipData.length == 3 && allValues && allValues[field] != zipData) {
            return zipData;
          }
        }

        return allValues ? allValues[field] : undefined;
      };

  zipDataDecorator = createDecorator(
      {
        field: "city",
        updates: {
          zip: this.updateZipDataFieldDecorator("zip")
        }
      },
      {
        field: "zip",
        updates: {
          city: this.updateZipDataFieldDecorator("city")
        }
      }
  );

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors = [errorUtils.invalidEmail];

    if (knownErrors.includes(errorCode)) {
      this.setErrorMessage(getMessage(`error.${errorCode}`))
    } else {
      this.setErrorMessage(getMessage('error.general'))
    }
  }

  handleSubmit = async (values: Partial<UpsertTestLaboratoryDto>) => {
    const {testLaboratoryId, onSave} = this.props;
    const {cancelTokenSource} = this.state;

    let zip = "";
    let city = "";

    if (values.zip) {
      if (values.zip.includes(', ')) {
        zip = values.zip.split(", ")[0];
      } else {
        zip = values.zip;
      }
    }

    if (values.city) {
      if (values.city.includes(', ')) {
        city = values.city.split(", ")[1];
      } else {
        city = values.city;
      }
    }

    const upsertTestLaboratoryDto = {
      ...values,
      zip: zip,
      city: city
    };

    if (testLaboratoryId) {
      await editTestLaboratory(testLaboratoryId, upsertTestLaboratoryDto, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => this.handleError(e.response.data))
    } else {
      await saveTestLaboratory(upsertTestLaboratoryDto, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => this.handleError(e.response.data))
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const {testLaboratory = this.initialTestLaboratoryValues()} = this.props;

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            decorators={[this.zipDataDecorator]}
            initialValues={testLaboratory}
            subscription={{pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({form, handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
              }
              <PraxFormGrid>
                {this.renderBasicInformationSection(form)}
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>
                {getMessage("button.save")}
              </Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}
              </Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

  renderBasicInformationSection = (form: FormApi): React.ReactNode => {

    const {cities, zip, countryOptions} = this.state;
    const {getMessage, countries} = this.props;

    const validator = (value: string) => required(value);

    const selectMap: IndexSignature = {
      city: cities,
      zip: zip,
      countryIso: countryOptions
    };
    // always set to CH like initial value
    const countryIso = form.getState().values.countryIso ? form.getState().values.countryIso : "CH";
    const country = countries.find(c => c.shortName === countryIso);
    const countryId = country!.id;

    const basicInfoFields = [
      textInput({
        name: "name",
        validate: validator
      }),
      textInput({
        name: "street"
      }),
      selectInput({
            name: "zip",
            allowAdditions: true,
            remoteSearch: true,
            customRemoteSearchMethod: ((searchQuery: string) => this.searchZipData(searchQuery, countryId)),
            onNewAddition: (this.onNewAddition("zip"))
          },
          selectMap
      ),
      selectInput({
            name: "city",
            allowAdditions: true,
            remoteSearch: true,
            customRemoteSearchMethod: ((searchQuery: string) => this.searchZipData(searchQuery, countryId)),
            onNewAddition: (this.onNewAddition("cities"))
          },
          selectMap
      ),
      selectInput({
            name: "countryIso"
          },
          selectMap
      )
    ];

    const extraInfoFields = [
      textInput({
        name: "email",
      }),
      textInput({
        name: "tel"
      }),
      textAreaInput({
        name: "remarks"
      })
    ]

    const mappedLeftRow = basicInfoFields.map((fieldProps, index) => {

      return (
          <Grid.Row key={index}>
            <Grid.Column width={4} verticalAlign="middle">
              {`${getMessage(`testLaboratory.${fieldProps.name}`)}:`}
            </Grid.Column>
            <Grid.Column width={12} verticalAlign="middle">
              <Field
                  fluid
                  {...fieldProps}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });

    const mappedRightRow = extraInfoFields.map((fieldProps, index) => {

      return (
          <Grid.Row key={index}>
            <Grid.Column width={4} verticalAlign="middle">
              {`${getMessage(`testLaboratory.${fieldProps.name}`)}:`}
            </Grid.Column>
            <Grid.Column width={12} verticalAlign="middle">
              <Field
                  fluid
                  {...fieldProps}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={8} verticalAlign="middle" textAlign="left">
              <Header as="h5">{getMessage("testLaboratory.basicInformation")}</Header>
            </Grid.Column>
            <Grid.Column width={8} textAlign="right">
              <Field
                  name="basicLaboratory"
                  component={CheckBox}
                  label={getMessage("testLaboratory.basicLaboratory")}
                  toggle
              />
              <Field
                  name="active"
                  component={CheckBox}
                  label={getMessage("active")}
                  toggle
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedLeftRow}
              </PraxFormGrid>
            </Grid.Column>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedRightRow}
              </PraxFormGrid>
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };

}

export default withAuthContext(withMessages(UpsertTestLaboratoryForm))
